import type {WrappedField} from '@cohort/merchants/components/form/FieldWrapper';
import BaseTextarea from '@cohort/merchants/components/form/textarea/BaseTextarea';
import type {FormField} from '@cohort/merchants/lib/form/utils';
import {updateLocalizedString} from '@cohort/merchants/lib/Utils';
import type {Language} from '@cohort/shared/schema/common';
import get from 'lodash/get';
import type {FieldValues} from 'react-hook-form';
import {useController} from 'react-hook-form';

type LocalizedTextareaProps<T extends FieldValues> = JSX.IntrinsicElements['textarea'] &
  WrappedField &
  FormField<T> & {
    selectedLanguage: Language;
  };

export default function LocalizedTextarea<T extends FieldValues>({
  register,
  control,
  rules,
  selectedLanguage,
  ...props
}: LocalizedTextareaProps<T>): JSX.Element {
  const {field, fieldState} = useController({
    control,
    name: props.name,
  });

  const errorMessage =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    get(fieldState, `error.${selectedLanguage}.message`) ?? get(fieldState, `error.message`);

  return (
    <BaseTextarea
      error={errorMessage}
      onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
        field.onChange(updateLocalizedString(selectedLanguage, field.value, event.target.value));
      }}
      // value of a controlled field should not be undefined
      value={field.value?.[selectedLanguage] ?? ''}
      {...props}
      ref={field.ref}
    />
  );
}
